<template>
  <div class="card">
    <h2 class="card-header">Dashboard</h2>
    
    <!-- Timestamp moved below header -->
    <div v-if="realtime.timestamp" class="text-xs text-center text-gray-500 mb-4">
      Last updated: {{ formatTimestamp }}
    </div>
    
    <!-- Dashboard Layout: Test Buttons | Attitude+Calibrate | Battery -->
    <div class="flex items-center justify-center gap-6">
      <!-- Left: Test Buttons -->
      <div class="flex-shrink-0" style="width: 180px;">
        <TestPanel />
      </div>
      
      <!-- Center: Attitude Indicator with Calibrate Button -->
      <div class="flex-1 flex flex-col items-center">
        <AttitudeIndicator />
        
        <!-- Calibrate Button with popover wrapper -->
        <div class="relative inline-flex flex-col items-center">
          <button
            @click="handleCalibrate"
            :disabled="!deviceStore.isConnected || isCalibrationUiLocked"
            class="mt-4 test-btn-small btn-primary flex items-center justify-center gap-2"
            :class="{ 'opacity-50 cursor-not-allowed': !deviceStore.isConnected || isCalibrationUiLocked }"
            style="width: 200px;"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
            <span v-if="!deviceStore.isCalibrationInProgress">Calibrate</span>
            <span v-else class="animate-pulse">Calibrating...</span>
          </button>

          <!-- Calibration Recommended Popover -->
          <div v-if="showCalibrationPopover" class="calibration-popover">
            <div class="popover-arrow"></div>
            <p class="font-semibold text-white mb-2">Calibration Recommended</p>
            <p class="text-sm text-gray-300 mb-2">
              If the device is secured in place and the vehicle is parked on a flat surface, the pitch and roll angles should be close to <strong class="text-white">0°</strong>.
            </p>
            <p class="text-sm text-gray-300 mb-3">
              Once the device is mounted, click the <strong class="text-white">Calibrate</strong> button to zero the device angles.
            </p>
            <div class="flex justify-center gap-3">
              <button class="popover-btn" @click="handlePopoverCalibrate">Calibrate now</button>
              <button class="popover-btn-secondary" @click="dismissCalibrationPopover">Skip</button>
            </div>
          </div>
        </div>
        
        <!-- Status message display -->
        <div v-if="message" class="mt-3 p-2 rounded text-xs w-full max-w-sm" :class="messageClass">
          {{ message }}
        </div>
      </div>
      
      <!-- Right: Battery Meter -->
      <div class="flex-shrink-0 flex flex-col items-center" style="width: 140px;">
        <h3 class="text-sm font-semibold text-gray-300 mb-3">Battery</h3>
        <div class="flex items-center gap-2">
          <!-- Battery value on left, overlaying slightly -->
          <div class="bg-[#13183b]/95 px-3 py-2 rounded-lg shadow-lg border border-[#2d3a70] -mr-6 z-20">
            <div class="text-2xl font-bold" :class="batteryTextClass">
              {{ formatBattery(realtime.battery) }}
            </div>
            <div class="text-xs text-gray-400 text-center">volts</div>
          </div>
          
          <!-- Vertical Bar Container -->
          <div class="relative w-20 h-64 bg-[#0a0e27] rounded-xl overflow-hidden shadow-lg border border-[#2d3a70] z-10">
            <!-- Tick marks -->
            <div class="absolute inset-0 flex flex-col justify-between py-2 px-1">
              <div class="h-0.5 bg-gray-600 w-full"></div>
              <div class="h-0.5 bg-gray-600 w-2/3 ml-auto"></div>
              <div class="h-0.5 bg-gray-600 w-full"></div>
              <div class="h-0.5 bg-gray-600 w-2/3 ml-auto"></div>
              <div class="h-0.5 bg-gray-600 w-full"></div>
            </div>
            
            <!-- Battery level bar (from bottom) -->
            <div
              class="absolute bottom-0 w-full rounded-b-xl transition-all duration-300"
              :style="{
                height: batteryPercent + '%',
                backgroundColor: batteryColor
              }"
            ></div>
          </div>
        </div>
        
        <p class="text-xs font-semibold mt-3" :class="batteryStatusClass">
          {{ batteryStatus }}
        </p>
        <p class="text-xs text-gray-500 mt-1">0-28V</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useDeviceStore } from '../stores/device.js';
import { useConfigStore } from '../stores/config.js';
import AttitudeIndicator from './AttitudeIndicator.vue';
import TestPanel from './TestPanel.vue';

const deviceStore = useDeviceStore();
const configStore = useConfigStore();

// Calibration popover
const showCalibrationPopover = ref(false);
let calibrationCheckTimer = null;

function scheduleCalibrationCheck() {
  clearCalibrationCheck();
  showCalibrationPopover.value = false;
  calibrationCheckTimer = setTimeout(() => {
    const { pitch, roll } = deviceStore.realtime;
    if (Math.abs(pitch) > 3 || Math.abs(roll) > 3) {
      showCalibrationPopover.value = true;
    }
  }, 3000);
}

function clearCalibrationCheck() {
  if (calibrationCheckTimer) {
    clearTimeout(calibrationCheckTimer);
    calibrationCheckTimer = null;
  }
}

function dismissCalibrationPopover() {
  showCalibrationPopover.value = false;
}

function handlePopoverCalibrate() {
  showCalibrationPopover.value = false;
  handleCalibrate();
}

// Trigger calibration check after a successful write to device
watch(() => configStore.lastSaved, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    scheduleCalibrationCheck();
  }
});

onUnmounted(() => clearCalibrationCheck());

// Calibrate handling
const message = ref('');
const messageType = ref('info');
const isCalibrationUiLocked = computed(() => (
  deviceStore.showCalibrationConfirmDialog
  || deviceStore.isCalibrationInProgress
  || deviceStore.showCalibrationCompleteDialog
));

const realtime = computed(() => deviceStore.realtime);
const batteryStatus = computed(() => {
  const status = deviceStore.batteryStatus;
  return status.charAt(0).toUpperCase() + status.slice(1);
});

// Calculate battery percentage (0-28V -> 0-100%)
const batteryPercent = computed(() => {
  return Math.min(100, (realtime.value.battery / 28) * 100);
});

const batteryColor = computed(() => {
  const status = deviceStore.batteryStatus;
  if (status === 'critical') return '#ef4444';
  if (status === 'low') return '#f59e0b';
  return '#22c55e';
});

const batteryTextClass = computed(() => {
  const status = deviceStore.batteryStatus;
  if (status === 'critical') return 'text-danger-600';
  if (status === 'low') return 'text-warning-600';
  return 'text-success-600';
});

const batteryStatusClass = computed(() => {
  const status = deviceStore.batteryStatus;
  if (status === 'critical') return 'text-danger-600';
  if (status === 'low') return 'text-warning-600';
  return 'text-success-600';
});

const messageClass = computed(() => {
  const classes = {
    success: 'bg-green-50 text-green-800 border border-green-300',
    error: 'bg-red-50 text-red-800 border border-red-300',
    info: 'bg-blue-50 text-blue-800 border border-blue-300'
  };
  return classes[messageType.value] || classes.info;
});

// Format functions
function formatBattery(voltage) {
  if (voltage === 0) return '--';
  return voltage.toFixed(1);
}

const formatTimestamp = computed(() => {
  if (!realtime.value.timestamp) return '';
  return realtime.value.timestamp.toLocaleTimeString();
});

function showMessage(text, type = 'info', duration = 3000) {
  message.value = text;
  messageType.value = type;
  
  if (duration > 0) {
    setTimeout(() => {
      message.value = '';
    }, duration);
  }
}

async function handleCalibrate() {
  if (!deviceStore.isConnected || isCalibrationUiLocked.value) return;
  deviceStore.requestCalibration();
}
</script>

<style scoped>
.test-btn-small {
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.5rem;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 44px;
}

.test-btn-small:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.test-btn-small:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
  border-color: #2563eb;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  border-color: #1d4ed8;
}

/* Calibration Popover */
.calibration-popover {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  width: 360px;
  background-color: #1a1f45;
  border: 1px solid rgba(239, 68, 68, 0.35);
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 0 20px rgba(239, 68, 68, 0.15), 0 10px 25px rgba(0, 0, 0, 0.4);
  z-index: 40;
}

.popover-arrow {
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background-color: #1a1f45;
  border-left: 1px solid rgba(239, 68, 68, 0.35);
  border-top: 1px solid rgba(239, 68, 68, 0.35);
}

.popover-btn {
  padding: 0.375rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: white;
  background-color: #3b82f6;
  border: 2px solid #2563eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.popover-btn:hover {
  background-color: #2563eb;
  border-color: #1d4ed8;
}

.popover-btn-secondary {
  padding: 0.375rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #9ca3af;
  background-color: transparent;
  border: 2px solid #3a4070;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.popover-btn-secondary:hover {
  color: #e5e7eb;
  border-color: #4a5590;
}
</style>
